import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash, UserPlus, ShieldCheck, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyEmployee = { name: "", email: "", password: "", role: "cpa" };

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmployee);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const params = showDeactivated ? "?include_deactivated=true" : "";
    const { data } = await api.get(`/employees${params}`);
    setEmployees(data);
  }, [showDeactivated]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added`);
      setOpen(false);
      setForm(emptyEmployee);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to add employee");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success(`${toDelete.name} deactivated`);
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to deactivate");
    }
  };

  const reactivate = async (emp) => {
    try {
      await api.post(`/users/${emp.id}/reactivate`);
      toast.success(`${emp.name} reactivated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const activeEmployees = employees.filter((e) => e.is_active !== false);
  const cpas = activeEmployees.filter((e) => e.role === "cpa").length;
  const admins = activeEmployees.filter((e) => e.role === "admin").length;
  const callers = activeEmployees.filter((e) => e.role === "caller").length;
  const docTeam = activeEmployees.filter((e) => e.role === "document_team").length;

  const roleLabel = (role) => ({
    admin: "Admin",
    cpa: "CPA",
    caller: "Caller",
    document_team: "Doc Team",
  }[role] || role);

  const roleBadgeClass = (role) => {
    if (role === "admin") return "bg-[#D32F2F]/10 text-[#D32F2F]";
    if (role === "caller") return "bg-amber-50 text-amber-700";
    if (role === "document_team") return "bg-emerald-50 text-emerald-700";
    return "bg-[#0A2540]/10 text-[#0A2540]";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="employees-title">Employees</h1>
          <p className="text-slate-600 mt-1">Firm staff — CPAs and administrators with dashboard access.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch id="emp-deact" checked={showDeactivated} onCheckedChange={setShowDeactivated} data-testid="employees-toggle-deactivated" />
            <Label htmlFor="emp-deact" className="text-sm text-slate-600 cursor-pointer">Show deactivated</Label>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="add-employee-btn">
              <UserPlus size={16} className="mr-1" /> Add employee
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add a new employee</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" />
              </div>
              <div>
                <Label>Work email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email" />
              </div>
              <div>
                <Label>Temporary password</Label>
                <Input type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="employee-password" />
                <div className="text-xs text-slate-500 mt-1">Share this out-of-band. Employee can reset via Forgot password.</div>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="employee-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cpa">CPA / Preparer</SelectItem>
                    <SelectItem value="caller">Caller</SelectItem>
                    <SelectItem value="document_team">Document Team</SelectItem>
                    <SelectItem value="admin">Admin (full access)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={saving || !form.name || !form.email || form.password.length < 6}
                className="bg-[#0A2540]"
                data-testid="employee-submit"
              >
                {saving ? "Adding…" : "Add employee"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Active</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#0A2540]" data-testid="employees-count">{activeEmployees.length}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">CPAs</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#0A2540]">{cpas}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Callers</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-amber-700">{callers}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Doc Team</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-emerald-700">{docTeam}</div>
        </div>
        <div className="rounded-md border tf-hairline bg-white p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Admins</div>
          <div className="mt-3 font-[Chivo] text-3xl font-black text-[#D32F2F]">{admins}</div>
        </div>
      </div>

      <div className="rounded-md border tf-hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">MFA</th>
              <th className="text-left p-3">Added</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-slate-500">No employees yet. Add your first team member.</td></tr>
            )}
            {employees.map((emp) => {
              const isActive = emp.is_active !== false;
              return (
              <tr key={emp.id} className={`border-t tf-hairline hover:bg-slate-50 ${isActive ? "" : "opacity-60"}`}>
                <td className="p-3 font-semibold text-[#0A2540]">{emp.name}</td>
                <td className="p-3 font-mono text-xs">{emp.email}</td>
                <td className="p-3">
                  <span className={`text-xs font-mono uppercase tracking-widest px-2 py-0.5 rounded ${roleBadgeClass(emp.role)}`}>
                    {roleLabel(emp.role)}
                  </span>
                  {!isActive && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">deactivated</span>}
                </td>
                <td className="p-3">
                  {emp.mfa_enabled ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><ShieldCheck size={12} weight="fill" /> On</span>
                  ) : (
                    <span className="text-xs text-slate-400">Off</span>
                  )}
                </td>
                <td className="p-3 font-mono text-xs">{(emp.created_at || "").split("T")[0]}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {isActive ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setToDelete(emp)}
                      data-testid={`remove-employee-${emp.id}`}
                    >
                      <Trash size={14} className="text-[#D32F2F]" />
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => reactivate(emp)} data-testid={`reactivate-employee-${emp.id}`}>
                      <ArrowClockwise size={14} className="text-[#0A2540]" />
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate {toDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Their login is revoked immediately. Historical work (returns, notices, tasks) stays intact and their name remains on those records. You can reactivate them at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-employee-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#D32F2F] hover:bg-[#D32F2F]/90" data-testid="remove-employee-confirm">
              Deactivate employee
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
